
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import {
    ArrowRight,
    Building2,
    ChevronRight,
    Compass,
    Hotel,
    MapPin,
    RefreshCw,
    Search,
    Sparkles,
} from "lucide-react";

import destImg from "../assests/images/hotel-bg.jpg";

const API_URL = "https://dubaibudgetivehotels.com/api/destinations";

const DESTINATION_IMAGES =
    "https://uaediscountedhotels.com/uploads/destinations/";

const containerClass =
    "mx-auto w-full max-w-[1536px] px-4 sm:px-6 lg:px-8";

/* =========================================================
   IMAGE HELPER
========================================================= */

function getDestinationImage(image) {
    if (!image) return destImg;

    const value =
        typeof image === "object"
            ? image.url || image.path || image.image || ""
            : image;

    if (!value || typeof value !== "string") {
        return destImg;
    }

    if (/^https?:\/\//i.test(value)) {
        return value;
    }

    if (value.startsWith("//")) {
        return `https:${value}`;
    }

    if (value.startsWith("/")) {
        return value;
    }

    return `${DESTINATION_IMAGES}${value.replace(
        /^uploads\/destinations\//,
        ""
    )}`;
}

/* =========================================================
   DESTINATION CARD
========================================================= */

function DestinationCard({
    destination,
    index,
    large = false,
    onOpen,
}) {
    const [imageError, setImageError] = useState(false);

    const image = imageError
        ? destImg
        : getDestinationImage(destination.image);

    const hotelCount = Number(destination.hotels_count) || 0;

    return (
        <button
            type="button"
            onClick={() => onOpen(destination)}
            aria-label={`Explore ${destination.name}`}
            className={`
        group relative block w-full overflow-hidden
        rounded-[22px] bg-[#064E3B] text-left
        shadow-[0_12px_35px_rgba(6,78,59,0.10)]
        transition-all duration-500
        hover:-translate-y-1
        hover:shadow-[0_25px_60px_rgba(6,78,59,0.20)]
        ${large
                    ? "h-[360px] sm:h-[440px] lg:h-[490px]"
                    : "h-[320px] sm:h-[360px]"
                }
      `}
        >
            {/* BACKGROUND IMAGE */}
            <img
                src={image}
                alt={destination.name || "Dubai destination"}
                loading={index > 2 ? "lazy" : "eager"}
                onError={() => setImageError(true)}
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
            />

            {/* DARK GREEN OVERLAY */}
            <div className="absolute inset-0 bg-gradient-to-t from-[#032F28]/95 via-[#064E3B]/40 to-[#032F28]/20" />

            {/* HOVER OVERLAY */}
            <div className="absolute inset-0 bg-[#064E3B]/0 transition-colors duration-500 group-hover:bg-[#064E3B]/15" />

            {/* TOP CONTENT */}
            <div className="absolute left-5 right-5 top-5 flex items-start justify-between gap-3 sm:left-7 sm:right-7 sm:top-7">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-white/25 bg-white/15 text-xs font-extrabold text-white backdrop-blur-md">
                    {String(index + 1).padStart(2, "0")}
                </span>

                <span className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/15 px-3.5 py-2 text-[11px] font-bold text-white backdrop-blur-md">
                    <Building2 size={15} className="text-[#A5E8B8]" />
                    {hotelCount} {hotelCount === 1 ? "Hotel" : "Hotels"}
                </span>
            </div>

            {/* BOTTOM CONTENT */}
            <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8">
                <p className="inline-flex items-center gap-2 text-[10px] font-extrabold uppercase tracking-[0.17em] text-[#B6F2CB]">
                    <MapPin size={14} />
                    Dubai, United Arab Emirates
                </p>

                <div className="mt-4 flex items-end justify-between gap-4">
                    <div className="min-w-0">
                        <h3
                            className={`font-extrabold leading-[1.12] tracking-tight text-white ${large
                                ? "text-[32px] sm:text-[43px] lg:text-[48px]"
                                : "text-[28px] sm:text-[33px]"
                                }`}
                        >
                            {destination.name}
                        </h3>

                        <p className="mt-3 text-[13px] leading-7 text-white/75">
                            Discover hotels and places to stay
                        </p>
                    </div>

                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#168B43] text-white shadow-lg transition-all duration-300 group-hover:-rotate-45 group-hover:bg-[#20A653]">
                        <ArrowRight size={21} />
                    </span>
                </div>

                {/* ANIMATED LINE */}
                <div className="mt-6 h-[2px] w-full overflow-hidden bg-white/20">
                    <div className="h-full w-0 bg-[#7CE3A1] transition-all duration-500 group-hover:w-full" />
                </div>
            </div>
        </button>
    );
}

/* =========================================================
   LOADING SKELETON
========================================================= */

function DestinationSkeleton({ large = false }) {
    return (
        <div
            className={`animate-pulse rounded-[22px] bg-[#DFEEE4] ${large ? "h-[440px] lg:h-[490px]" : "h-[320px] sm:h-[360px]"
                }`}
        />
    );
}

/* =========================================================
   MAIN COMPONENT
========================================================= */

export default function DestinationsList({
    showBanner = true,
    showHeading = true,
}) {
    const navigate = useNavigate();

    const [destinations, setDestinations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    /* =====================================================
       FETCH DESTINATIONS
    ===================================================== */

    const fetchDestinations = useCallback(async (signal) => {
        setLoading(true);
        setError(false);

        try {
            const response = await axios.get(API_URL, {
                signal,
            });

            if (signal?.aborted) return;

            const data = response.data;

            setDestinations(Array.isArray(data) ? data : []);
        } catch (fetchError) {
            if (
                signal?.aborted ||
                fetchError.code === "ERR_CANCELED"
            ) {
                return;
            }

            console.error("Failed to fetch destinations:", fetchError);
            setError(true);
            setDestinations([]);
        } finally {
            if (!signal?.aborted) {
                setLoading(false);
            }
        }
    }, []);

    useEffect(() => {
        const controller = new AbortController();

        fetchDestinations(controller.signal);

        return () => {
            controller.abort();
        };
    }, [fetchDestinations]);

    /* =====================================================
       NAVIGATION
    ===================================================== */

    const openDestination = (destination) => {
        if (!destination.slug) return;

        navigate(
            `/destinations/${encodeURIComponent(destination.slug)}`
        );

        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    };

    const destinationCount = destinations.length;

    /* =====================================================
       RENDER
    ===================================================== */

    return (
        <main className="w-full overflow-hidden bg-[#F5FAF7] text-[#17324D]">
            {/* =================================================
          HERO BANNER
      ================================================= */}

            {showBanner && (
                <section className="relative isolate overflow-hidden bg-[#064E3B] pt-28 text-white sm:pt-32">
                    {/* BACKGROUND IMAGE */}
                    <div className="absolute inset-0 -z-20">
                        <img
                            src={destImg}
                            alt=""
                            className="h-full w-full object-cover"
                        />
                    </div>

                    {/* GREEN OVERLAY */}
                    <div className="absolute inset-0 -z-10 bg-gradient-to-r from-[#032F28]/95 via-[#064E3B]/85 to-[#064E3B]/65" />

                    {/* DECORATIVE CIRCLES */}
                    <div className="pointer-events-none absolute -right-24 -top-24 h-[440px] w-[440px] rounded-full border border-white/10" />

                    <div className={`${containerClass} relative pb-16 pt-4 sm:pb-20`}>
                        {/* BREADCRUMBS */}
                        <nav
                            aria-label="Breadcrumb"
                            className="flex flex-wrap items-center gap-2 text-xs font-semibold text-white/70"
                        >
                            <Link
                                to="/"
                                className="transition hover:text-white"
                            >
                                Home
                            </Link>

                            <ChevronRight size={14} />

                            <span className="text-[#B6F2CB]">
                                Destinations
                            </span>
                        </nav>

                        {/* HERO CONTENT */}
                        <div className="mt-11 max-w-[800px]">
                            <div className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-2 text-[11px] font-extrabold uppercase tracking-[0.16em] text-[#B6F2CB] backdrop-blur-sm">
                                <Sparkles size={15} />
                                Explore Dubai
                            </div>

                            <h1 className="mt-6 text-[40px] font-extrabold leading-[1.12] tracking-tight text-white sm:text-[56px] lg:text-[68px]">
                                Discover Dubai's
                                <br />
                                <span className="text-[#A5E8B8]">
                                    Best Destinations.
                                </span>
                            </h1>

                            <p className="mt-6 max-w-[630px] text-[14px] leading-8 text-white/80 sm:text-[16px]">
                                From iconic city districts to beautiful
                                beachfront neighborhoods, explore Dubai's
                                destinations and find the perfect hotel
                                for your next stay.
                            </p>

                            <div className="mt-8 flex flex-wrap items-center gap-3">
                                <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2.5 text-xs font-bold text-white backdrop-blur-sm">
                                    <MapPin
                                        size={16}
                                        className="text-[#A5E8B8]"
                                    />
                                    Dubai, UAE
                                </span>

                                <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2.5 text-xs font-bold text-white backdrop-blur-sm">
                                    <Compass
                                        size={16}
                                        className="text-[#A5E8B8]"
                                    />
                                    Explore Neighborhoods
                                </span>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* =================================================
          DESTINATIONS SECTION
      ================================================= */}

            <section className={`${containerClass} py-14 sm:py-20`}>
                {/* SECTION HEADING */}
                {showHeading && (
                    <div className="mb-10 flex flex-col justify-between gap-6 lg:mb-12 lg:flex-row lg:items-end">
                        <div className="max-w-[800px]">
                            <p className="inline-flex items-center gap-3 text-[11px] font-extrabold uppercase tracking-[0.17em] text-[#168B43]">
                                <span className="h-[2px] w-8 bg-[#168B43]" />
                                Discover Dubai
                            </p>

                            <h2 className="mt-4 text-[34px] font-extrabold leading-[1.15] tracking-tight text-[#17324D] sm:text-[46px] lg:text-[54px]">
                                Explore Popular{" "}
                                <span className="text-[#168B43]">
                                    Destinations.
                                </span>
                            </h2>

                            <p className="mt-5 max-w-[690px] text-[14px] leading-8 text-[#718096]">
                                Discover Dubai's distinctive neighborhoods,
                                explore accommodation options and find
                                the location that suits your next trip.
                            </p>
                        </div>

                        {!loading && !error && destinationCount > 0 && (
                            <div className="flex w-fit items-center gap-4 rounded-2xl border border-[#DCEAE1] bg-white px-5 py-4 shadow-sm">
                                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#E8F7EE] text-[#168B43]">
                                    <MapPin size={24} />
                                </div>

                                <div>
                                    <p className="text-[25px] font-extrabold leading-none text-[#17324D]">
                                        {String(destinationCount).padStart(2, "0")}
                                    </p>

                                    <p className="mt-1 text-[11px] font-bold text-[#718096]">
                                        Dubai Destinations
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* LOADING STATE */}
                {loading ? (
                    <>
                        <div className="grid gap-5 lg:grid-cols-12">
                            <div className="lg:col-span-7">
                                <DestinationSkeleton large />
                            </div>

                            <div className="lg:col-span-5">
                                <DestinationSkeleton large />
                            </div>
                        </div>

                        <div className="mt-5 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                            {[1, 2, 3].map((item) => (
                                <DestinationSkeleton key={item} />
                            ))}
                        </div>
                    </>
                ) : error ? (
                    /* ERROR STATE */
                    <div className="rounded-[24px] border border-[#DCEAE1] bg-white px-6 py-16 text-center sm:py-20">
                        <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-[22px] bg-[#E8F7EE] text-[#168B43]">
                            <RefreshCw size={34} />
                        </div>

                        <h3 className="mt-6 text-[26px] font-extrabold text-[#17324D]">
                            Unable To Load Destinations
                        </h3>

                        <p className="mx-auto mt-4 max-w-[450px] text-sm leading-8 text-[#718096]">
                            Something went wrong while loading the
                            destinations. Please try again.
                        </p>

                        <button
                            type="button"
                            onClick={() => fetchDestinations()}
                            className="mt-7 inline-flex items-center gap-2 rounded-xl bg-[#168B43] px-6 py-3.5 text-sm font-extrabold text-white transition hover:bg-[#087D37]"
                        >
                            <RefreshCw size={17} />
                            Try Again
                        </button>
                    </div>
                ) : destinationCount === 0 ? (
                    /* EMPTY STATE */
                    <div className="rounded-[24px] border border-[#DCEAE1] bg-white px-6 py-16 text-center sm:py-20">
                        <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-[22px] bg-[#E8F7EE] text-[#168B43]">
                            <MapPin size={35} />
                        </div>

                        <h3 className="mt-6 text-[26px] font-extrabold text-[#17324D]">
                            No Destinations Available
                        </h3>

                        <p className="mx-auto mt-4 max-w-[450px] text-sm leading-8 text-[#718096]">
                            We couldn't find any destinations right now.
                            Please check again shortly.
                        </p>

                        <button
                            type="button"
                            onClick={() => navigate("/hotels")}
                            className="mt-7 inline-flex items-center gap-2 rounded-xl bg-[#168B43] px-6 py-3.5 text-sm font-extrabold text-white transition hover:bg-[#087D37]"
                        >
                            Browse Hotels
                            <ArrowRight size={17} />
                        </button>
                    </div>
                ) : (
                    <>
                        {/* FEATURED DESTINATIONS */}
                        <div className="grid gap-5 lg:grid-cols-12">
                            {destinations.slice(0, 2).map((destination, index) => (
                                <div
                                    key={destination.id ?? destination.slug ?? index}
                                    className={
                                        index === 0
                                            ? "lg:col-span-7"
                                            : "lg:col-span-5"
                                    }
                                >
                                    <DestinationCard
                                        destination={destination}
                                        index={index}
                                        large
                                        onOpen={openDestination}
                                    />
                                </div>
                            ))}
                        </div>

                        {/* REMAINING DESTINATIONS */}
                        {destinationCount > 2 && (
                            <div className="mt-5 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                                {destinations.slice(2).map((destination, index) => (
                                    <DestinationCard
                                        key={
                                            destination.id ??
                                            destination.slug ??
                                            index + 2
                                        }
                                        destination={destination}
                                        index={index + 2}
                                        onOpen={openDestination}
                                    />
                                ))}
                            </div>
                        )}
                    </>
                )}
            </section>

            {/* =================================================
          BOTTOM CTA SECTION
      ================================================= */}

            {!loading && !error && destinationCount > 0 && (
                <section className="relative overflow-hidden bg-[#064E3B] py-14 text-white sm:py-16">
                    <div className="pointer-events-none absolute -right-20 -top-20 h-[320px] w-[320px] rounded-full border border-white/10" />

                    <div className={`${containerClass} relative`}>
                        <div className="flex flex-col items-start justify-between gap-8 lg:flex-row lg:items-center">
                            <div className="flex items-start gap-5">
                                <div className="hidden h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-white/20 bg-white/10 text-[#B6F2CB] sm:flex">
                                    <Hotel size={27} />
                                </div>

                                <div>
                                    <p className="inline-flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-[0.17em] text-[#B6F2CB]">
                                        <Sparkles size={15} />
                                        Your Dubai Stay Starts Here
                                    </p>

                                    <h2 className="mt-3 text-[28px] font-extrabold leading-tight text-white sm:text-[38px]">
                                        Ready To Discover Your Next Stay?
                                    </h2>

                                    <p className="mt-4 max-w-[650px] text-sm leading-7 text-white/75">
                                        Explore Dubai hotels, compare your
                                        accommodation options and find a stay
                                        that fits your travel plans.
                                    </p>
                                </div>
                            </div>

                            <button
                                type="button"
                                onClick={() => navigate("/hotels")}
                                className="inline-flex shrink-0 items-center justify-center gap-3 rounded-xl bg-[#168B43] px-7 py-4 text-[13px] font-extrabold text-white shadow-lg transition hover:-translate-y-0.5 hover:bg-[#20A653]"
                            >
                                Browse All Hotels
                                <ArrowRight size={18} />
                            </button>
                        </div>
                    </div>
                </section>
            )}
        </main>
    );
}
